// lib/DataContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, JSX } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase/client";

// -----------------------------
// Types
// -----------------------------
interface DataContextType {
  data: Record<string, any>;
  setData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

interface DataProviderProps {
  children: ReactNode;
}

// -----------------------------
// Context
// -----------------------------
const DataContext = createContext<DataContextType | undefined>(undefined);

// -----------------------------
// Provider
// -----------------------------
export function DataProvider({ children }: DataProviderProps): JSX.Element {
  const { user } = useAuth(); // Optional: tie data to the authenticated user
  const [data, setData] = useState<Record<string, any>>(() => {
    if (typeof window !== "undefined") {
      const savedData = localStorage.getItem("data");
      return savedData ? JSON.parse(savedData) : {};
    }
    return {};
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem("data", JSON.stringify(data));
  }, [data]);

  // Optional: Fetch user-specific data from Supabase
  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      const { data: supabaseData, error } = await supabase
        .from("users") // Replace with your actual table
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (!error && supabaseData) {
        setData(supabaseData);
      }
    };

    fetchUserData();
  }, [user]);

  return (
    <DataContext.Provider value={{ data, setData }}>
      {children}
    </DataContext.Provider>
  );
}

// -----------------------------
// Hook
// -----------------------------
export function useData(): DataContextType {
  const context = useContext(DataContext);
  if (!context) throw new Error("useData must be used within a DataProvider");
  return context;
}
